// Milestone 18/19 (Decision Surface / Decision Flow) defined these
// directly inside SahibBriefingPage.js (real filename, predates the
// Martian rebrand) for its own "Evidence & reasoning" disclosure --
// kept there unchanged (an existing test, SahibDecisionSurface.test.js,
// asserts on the literal `const EVIDENCE_LABELS = {` declaration in
// that specific file, so it is deliberately left alone rather than
// converted to an import).
//
// Milestone 36 (Talk to Martian: Intelligence-First Chat UX) needs the
// SAME real key->label vocabulary and value formatting rule for Chat's
// own "Evidence & reasoning" disclosure (Section 5: "Reuse the
// established M18/M19... pattern"). Rather than reach into
// SahibBriefingPage.js's module-private consts, this file holds the
// same real mapping (plus the additional ChatGPT Ads observation keys
// Chat's evidence can show, which SahibBriefingPage.js's own dashboard
// never displays) as TalkToMartianPanel.js's single source for this.
export const EVIDENCE_LABELS = {
  current_visibility: 'Current visibility', target: 'Target visibility',
  competitor: 'Competitor', competitor_visibility: 'Competitor visibility', brand_visibility: 'Your visibility', gap: 'Visibility gap',
  brand_citations: 'Your citations', total_citations: 'Total citations tracked', share: 'Your citation share',
  leading_competitor: 'Leading competitor',
  brand_source_domains: 'Distinct sources citing you', minimum_recommended: 'Recommended minimum',
  recommendation_rate: 'Recommendation rate',
  executions: 'Checks run so far',
  // Meta Paid Growth (meta_intelligence.py's MetaFindingService).
  campaigns_affected: 'Campaigns affected', campaigns_checked: 'Campaigns checked',
  campaign_names: 'Campaigns', average_roas: 'Average ROAS',
  // Commerce (commerce_intelligence.py's CommerceFindingService).
  unavailable_count: 'Out of stock', products_checked: 'Products checked', product_names: 'Products',
  // Milestone 36 -- ChatGPT Ads observation fields, real keys from
  // observe_campaign()'s own return value (chatgpt_ads_observation.py),
  // shown in Talk to Martian's own evidence disclosure the same way.
  spend: 'Spend', impressions: 'Impressions', clicks: 'Clicks', conversions: 'Conversions',
  ctr: 'CTR', cpc: 'CPC', cpa: 'CPA',
  // Milestone 42 (AI CGO Workspace) -- real evidence keys "What Martian
  // Found" surfaces for the first time: AI Search's own overview
  // (services.py's VisibilityService.client_overview), and Brand
  // Growth/Creative/Creator's own real detect_for_client() evidence
  // dicts (brand_growth.py/creative_intelligence.py/creator_command.py,
  // Milestone 38). Every key here is a real, already-existing field name
  // read straight off those services' own real return values -- nothing
  // invented for display purposes.
  total_executions: 'AI checks run', visibility_rate: 'Visibility rate', avg_position: 'Average position',
  citation_share: 'Citation share', source_diversity: 'Distinct sources',
  growth_state: 'Growth state', readiness: 'Readiness',
  open_opportunities: 'Open opportunities', governance_status: 'Governance status',
  strategy_status: 'Strategy status', channel: 'Channel', platform: 'Platform',
  risk: 'Risk', effort: 'Effort', reversibility: 'Reversibility', priority_source: 'Priority source',
  current_stage: 'Current stage', blockers: 'Blockers', command_status: 'Command status',
};

// Milestone 18 -- keys that are either internal linkage (never meant for
// display) or already shown elsewhere in the surrounding UI. Milestone
// 42 adds entity_type/entity_id (Brand Growth's own internal linkage,
// same reasoning as campaign_entity_ids).
export const EVIDENCE_SKIP_KEYS = new Set(['campaign_entity_ids', 'format_rationale', 'entity_type', 'entity_id']);

// Milestone 42 -- a real evidence value is only ever shown generically
// when it is a primitive, or an array of primitives (campaign_names/
// product_names/blockers) -- never an array of objects (AI Search's own
// `competitors` field) or a nested object, which this generic renderer
// cannot turn into a readable line without inventing a shape. This is
// never reached for a domain that has its own dedicated, hand-built
// display (e.g. Social's connected-channels list further down the page)
// -- it only guards the generic "Evidence & reasoning" grid.
export function isDisplayableEvidenceValue(value) {
  if (value == null) return false;
  // An empty array (e.g. AI Search's own real `competitors: []` when
  // none were detected) carries no real evidence to show -- rendering it
  // as a blank row would be worse than simply omitting it.
  if (Array.isArray(value)) return value.length > 0 && value.every((v) => v == null || typeof v !== 'object');
  return typeof value !== 'object';
}

export function formatEvidenceValue(key, value) {
  // Milestone 18 -- Meta's campaign_names/Commerce's product_names are
  // real arrays of strings (not the numeric/percentage shape every prior
  // evidence field had) -- joined into one readable line rather than
  // Array.prototype.toString's unreadable comma-run.
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value !== 'number') return String(value);
  // average_roas is a real ratio (meta_intelligence.py), not a percent or
  // plain count -- shown as "0.36x" so it isn't misread as a percentage.
  if (/roas/i.test(key)) return `${value}x`;
  // Every other numeric evidence field is either a percentage or a plain
  // count -- the visibility/rate/share/target/gap naming convention
  // already used throughout services.py's opportunity/diagnostic
  // evidence dicts tells us which.
  const isPercent = /visibility|rate|share|target|gap/i.test(key);
  return isPercent ? `${value}%` : String(value);
}
